import { Typography } from "@mui/material";
import React from "react";
import { toast } from "react-toastify";
import { makeStyles } from "tss-react/mui";
import speechLoadingIcon from "../../../assets/images/quiz/speech-loading.gif";
import speechPauseIcon from "../../../assets/images/quiz/speech-pause.svg";
import speechPlayIcon from "../../../assets/images/quiz/speech-play.svg";
import { useAppDispatch, useAppSelector } from "../../../store/hook";
import { studyActions } from "../../../store/study/studySlice";
import * as studySlice from "../../../store/study/studySlice";
import { getSpeechConfig, textToSpeech } from "../../../store/quiz/quizBaseApi";
import { generateUUID } from "../../../utils";

const useStyles = makeStyles()((theme) => ({
  root: {
    margin: theme.spacing(2, 0),
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    [theme.breakpoints.up("sm")]: {
      flexDirection: "row", // Switch to row for screens larger than 'sm'
    },
  },
  wordDetailsContainer: {
    width: "90%",
  },
  wordLine: {
    marginBottom: theme.spacing(0.5),
  },
  word: {
    fontWeight: "700",
    marginRight: theme.spacing(1),
    color: theme.palette.common.highlight,
  },
  glossary: {
    fontWeight: "700",
  },
  mainWordHighlight: {
    fontWeight: "700",
    marginRight: theme.spacing(1),
    color: theme.palette.common.highlight,
    background: "rgba(255, 199, 74, 0.2)",
    border: "1px solid #FFC74A",
    borderRadius: "10px",
    padding: "8px",
  },
  highlight: {
    marginRight: theme.spacing(1),
    color: theme.palette.common.black,
    background: "rgba(255, 199, 74, 0.2)",
    border: "1px solid #FFC74A",
    borderRadius: "10px",
    padding: "8px",
    lineHeight: "2.6",
  },
  normal: {
    marginRight: theme.spacing(0.5),
  },
  synonyms: {
    color: theme.palette.text.secondary,
    // fontStyle: "italic",
    fontSize: "14px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "10px", // Extra small devices
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "12px", // Small devices
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "14px", // Medium devices (default)
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "16px", // Large devices
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "18px", // Extra large devices
    },
  },
  hearButtonContainer: {
    display: "flex",
    alignItems: "start",
  },
  button: {
    marginLeft: "10px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    transition: "transform 0.1s ease, box-shadow 0.1s ease",
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
    "&:not(:disabled):active": {
      transform: "scale(0.95)",
    },
  },
}));

const WordDetailImpl = ({ wordDetails, onPlaybackStart }) => {
  const dispatch = useAppDispatch();
  const setAudioPlaying = React.useCallback(
    (payload) => dispatch(studyActions.setAudioPlaying(payload)),
    [dispatch],
  );
  const myWord = wordDetails.text || wordDetails.word;
  const isOtherAudioPlaying = useAppSelector((state) => {
    const ap = studySlice.audioPlaying(state);
    return Boolean(ap) && ap !== myWord;
  });
  const [audioUrl, setAudioUrl] = React.useState(null);
  const [audioUrls, setAudioUrls] = React.useState([]);
  const [contentArray, setContentArray] = React.useState([]);
  const [contentIndex, setContentIndex] = React.useState(0);
  const [currentContent, setCurrentContent] = React.useState();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [fullSentancePlaying, setFullSentancePlaying] = React.useState(false);
  const [contentArrayMap, setContentArrayMap] = React.useState([]);
  const student = JSON.parse(localStorage.getItem("profile")) || {};
  const { classes } = useStyles();

  const audioRef = React.useRef(null);
  const played = React.useRef(false);
  const playbackStarted = React.useRef(false);
  const playbackCompleted = React.useRef(false);
  const words = React.useRef([]);
  const referenceId = React.useRef(generateUUID());
  const sentanceAudio = React.useRef("");
  const timeout = React.useRef(null);

  const glossary = React.useMemo(() => {
    const def = wordDetails.definition;
    if (typeof def === "string") return def;
    if (Array.isArray(def)) return def.join(" ");
    return "";
  }, [wordDetails]);
  const synonymList = React.useMemo(
    () =>
      (wordDetails?.synonyms || []).filter(
        (syn) => !String(syn).includes("default"),
      ),
    [wordDetails],
  );

  const togglePlayPause = () => {
    if (!currentContent) {
      // setCurrentContent(audioUrls[contentIndex]['word']);
      setCurrentContent({
        word: audioUrls[contentIndex]["word"],
        index: audioUrls[contentIndex]["index"],
        type: audioUrls[contentIndex]["category"],
      });
    }

    playbackCompleted.current = false;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
      played.current = true;
      setAudioPlaying(wordDetails.text || wordDetails.word);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);

    if (!playbackStarted.current) {
      playbackStarted.current = true;
      if (onPlaybackStart && typeof onPlaybackStart === "function") {
        onPlaybackStart();
      }
    }

    setAudioPlaying(wordDetails.text || wordDetails.word);
  };

  const handlePause = () => {
    setIsPlaying(false);
    setAudioPlaying("");
  };

  const handleFullSentancePlayback = () => {
    setFullSentancePlaying(true);
    setAudioPlaying(wordDetails.text || wordDetails.word);

    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }

    if (sentanceAudio.current && sentanceAudio.current.length) {
      // new s3 endpoint change --- minimal fix for audio play
      const correctedAudioUrl = sentanceAudio.current.replace(
        "knowledgebasechat.s3.eu-west-1.amazonaws.com",
        "knowledgebasechat.s3-us-west-1.amazonaws.com",
      );

      let audio = new Audio(correctedAudioUrl);
      // let audio = new Audio(sentanceAudio.current);
      audio.onended = () => {
        setFullSentancePlaying(false);
        setAudioPlaying("");
      };
      audio.play();
    } else {
      timeout.current = setTimeout(() => handleFullSentancePlayback(), 500);
    }
  };

  const handleEnd = () => {
    if (contentIndex < contentArrayMap.length - 1) {
      let next = audioUrls[contentIndex + 1];
      setContentIndex((prevIndex) => prevIndex + 1);
      setAudioUrl(next.url);
      // setCurrentContent(next.word);
      setCurrentContent({
        word: next.word,
        index: next.index,
        type: next.category,
      });
    } else {
      // After playback completions of all the words, resetting the playback queue
      setContentIndex(0);
      setAudioUrl(audioUrls[0].url);
      setCurrentContent("");
      setIsPlaying(false);

      played.current = false;
      playbackCompleted.current = true;

      handleFullSentancePlayback();
    }
  };


  // Will handle the next word playback
  React.useEffect(() => {
    if (
      isLoading ||
      !audioUrls.length ||
      !played.current ||
      !audioUrl ||
      playbackCompleted.current
    )
      return;

    togglePlayPause();
  }, [audioUrl]);

  React.useEffect(() => {
    if (!wordDetails || !Object.keys(wordDetails).length) return;

    const mainText = wordDetails.text || wordDetails.word || "";
    if (!mainText) return;

    let words = [`${mainText}`, glossary];

    let contentMap = mainText.split(" ");
    contentMap = contentMap.concat(glossary.split(" "));

    if (synonymList.length) {
      words.push(`Synonyms: ${synonymList.join(", ")}`);
      contentMap = contentMap.concat(["Synonyms:"]);
      // Keep each synonym as a single unit so multi-word synonyms ("jolly up")
      // map to exactly one audio token instead of one per word.
      contentMap = contentMap.concat(synonymList);
    }

    setContentArrayMap(contentMap.filter((e) => e));
    setContentArray(words);
  }, []);

  React.useEffect(() => {
    if (!contentArray.length || !contentArrayMap.length) return;
    let wordsReceived = 0;

    const handleReceiveAudio = (data) => {
      try {
        let jsonString;
        if (data instanceof ArrayBuffer) {
          const decoder = new TextDecoder("utf-8");
          jsonString = decoder.decode(data);
        } else {
          jsonString = data.toString("utf-8");
        }

        const parsedData = JSON.parse(jsonString);
        const {
          status,
          word,
          audio: audioBase64,
          error: receivedError,
          reference_id,
        } = parsedData;

        if (status === "error") {
          const errorMessage = receivedError || "Unknown error occurred.";
          toast.error(`Error received: ${errorMessage}`);
          socketConn.disconnect();
          return;
        }

        if (word && audioBase64 && referenceId.current == reference_id) {
          const audioData = new Uint8Array(
            atob(audioBase64)
              .split("")
              .map((char) => char.charCodeAt(0)),
          );
          const blob = new Blob([audioData], { type: "audio/wav" });

          words.current.push({ word, blob });
          wordsReceived++;
        }

        let definitionIndex = 0;
        let synonymIndex = 0;

        if (wordsReceived >= contentArrayMap.length) {
          const mainText = wordDetails.text || wordDetails.word || "";
          const mainWordCount = mainText ? mainText.split(" ").length : 0;
          const glossaryWordCount = glossary ? glossary.split(" ").length : 0;
          let _Urls = contentArrayMap.map((word, index) => {
            // The "Synonyms:" label is its own audio token but is not a synonym,
            // so it must not consume a synonymIndex — otherwise every real
            // synonym's highlight is shifted by one.
            if (word === "Synonyms:") {
              let _word = words.current.find((e) => e.word == word);
              return {
                word,
                url: URL.createObjectURL(_word.blob),
                category: "label",
                index,
              };
            }

            let category =
              index < mainWordCount
                ? "word"
                : index < mainWordCount + glossaryWordCount
                  ? "definition"
                  : "synonym";

            if (category === "definition") {
              let _word = words.current.find((e) => e.word == word);
              return {
                word,
                url: URL.createObjectURL(_word.blob),
                category,
                index: definitionIndex++,
              };
            }
            if (category === "synonym") {
              let _word = words.current.find((e) => e.word == word);
              return {
                word,
                url: URL.createObjectURL(_word.blob),
                category,
                index: synonymIndex++,
              };
            }
            let _word = words.current.find((e) => e.word == word);
            return {
              word,
              url: URL.createObjectURL(_word.blob),
              category,
              index,
            };
          });

          setAudioUrls(_Urls);
          setAudioUrl(_Urls[contentIndex].url);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error processing the received message:", err);
      }
    };

    const socketConn = window?.socketConn;
    if (!socketConn) {
      console.error("Socket connection is not available.");
      return;
    }

    getSpeechConfig(
      String(student.geographicalType).toLowerCase(),
      student.learningGeneration,
    ).then(async (conf) => {
      const res = await textToSpeech({
        text: [wordDetails.text, glossary].join("\n"),
        accent: conf.accent,
        gender: conf.gender.charAt(0).toUpperCase() + conf.gender.slice(1),
      });

      if (res?.response?.audio) {
        sentanceAudio.current = res.response.audio;
      }

      // Handle server responses
      socketConn.on("receiveAudio", handleReceiveAudio);

      // Emit the text to start processing
      // console.log("Sending text:", content);
      socketConn.emit("sendText", {
        text: contentArray.join("\n"),
        // Explicit tokens preserve multi-word synonyms as single audio units,
        // matching contentArrayMap exactly so the received count lines up.
        tokens: contentArrayMap,
        accent: conf.accent.slice(3),
        gender: String(conf.gender).toUpperCase(),
        reference_id: referenceId.current,
      });
      socketConn.on("close", () => {
        socketConn.off("receiveAudio", handleReceiveAudio);
      });
    });
  }, [contentArray, contentArrayMap]);

  return (
    <div className={classes.root}>
      <div className={classes.hearButtonContainer}>
        <button
          className={classes.button}
          onClick={togglePlayPause}
          disabled={isLoading || fullSentancePlaying || isOtherAudioPlaying}
        >
          {(() => {
            const showLoading = isLoading || !Object.keys(audioUrls).length;
            const iconSrc = showLoading
              ? speechLoadingIcon
              : isPlaying
                ? speechPauseIcon
                : speechPlayIcon;
            return (
              <img
                style={{ marginRight: "1rem" }}
                height={showLoading ? "50px" : undefined}
                src={iconSrc}
              />
            );
          })()}
        </button>

        <audio
          ref={audioRef}
          src={audioUrl}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnd}
        />
      </div>
      <div className={classes.wordDetailsContainer}>
        <Typography className={classes.wordLine}>
          <span
            className={fullSentancePlaying ? classes.mainWordHighlight : ""}
          >
            <span
              className={
                currentContent &&
                currentContent.word == wordDetails.text &&
                currentContent.type === "word"
                  ? classes.mainWordHighlight
                  : classes.word
              }
            >
              {wordDetails.text}:
            </span>
            {glossary.split(" ").map((word, index) => (
              <span
                key={index}
                className={
                  (currentContent &&
                  currentContent.index == index &&
                  currentContent.word === word &&
                  currentContent.type === "definition"
                    ? classes.highlight
                    : classes.normal) +
                  " " +
                  classes.glossary
                }
              >
                {word}
              </span>
            ))}
          </span>
        </Typography>
        {synonymList.length > 0 && (
          <Typography className={classes.synonyms}>
            <span>Synonyms: </span>
            {synonymList.map((syn, index) => {
              const isHighlighted =
                currentContent &&
                currentContent.type === "synonym" &&
                currentContent.index === index;
              return (
                <span
                  key={index}
                  className={isHighlighted ? classes.highlight : classes.normal}
                >
                  {syn}
                  {index < synonymList.length - 1 ? ", " : ""}
                </span>
              );
            })}
          </Typography>
        )}
      </div>
    </div>
  );
};

export const WordDetail = React.memo(WordDetailImpl);
